import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for drag-to-scroll functionality
 * @param {Object} containerRef - Ref object for the scrollable container
 * @param {string} draggingClassName - CSS class name to apply when dragging (optional)
 * @returns {Object} - Object containing { ref, isDragging }
 */
export const useDragScroll = (containerRef, draggingClassName = null) => {
    const [isDragging, setIsDragging] = useState(false);
    const cssModuleRef = useRef(null);

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        // Store CSS module reference if provided (for classNames with CSS modules)
        if (typeof draggingClassName === 'object' && draggingClassName?.dragging) {
            cssModuleRef.current = draggingClassName;
        }

        let rafId = null;
        let dragging = false;
        let startX = 0;
        let scrollLeft = 0;
        let hasScrolled = false;

        const onMouseDown = (e) => {
            // Handle left (0) and right (2) mouse buttons
            if (e.button !== 0 && e.button !== 2) return;

            // Check if container is scrollable
            const hasScroll = container.scrollWidth > container.clientWidth;
            if (!hasScroll) return;

            e.preventDefault();
            e.stopImmediatePropagation();
            dragging = true;
            hasScrolled = false;
            setIsDragging(true);
            startX = e.pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;

            // Add dragging class
            if (cssModuleRef.current?.dragging) {
                container.classList.add(cssModuleRef.current.dragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.add(draggingClassName);
            }
        };

        const onMouseLeave = () => {
            dragging = false;
            setIsDragging(false);
            hasScrolled = false;

            // Remove dragging class
            if (cssModuleRef.current?.dragging) {
                container.classList.remove(cssModuleRef.current.dragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.remove(draggingClassName);
            }
        };

        const onMouseUp = (e) => {
            if (dragging) {
                e.stopImmediatePropagation();
            }

            dragging = false;
            setIsDragging(false);

            // Remove dragging class
            if (cssModuleRef.current?.dragging) {
                container.classList.remove(cssModuleRef.current.dragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.remove(draggingClassName);
            }

            // Reset hasScrolled after a short delay to allow click handlers to check it
            setTimeout(() => {
                hasScrolled = false;
            }, 100);
        };

        const onMouseMove = (e) => {
            if (!dragging) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
                const x = e.pageX - container.offsetLeft;
                const walk = (x - startX) * 1;
                const newScrollLeft = scrollLeft - walk;

                if (Math.abs(newScrollLeft - scrollLeft) > 2) {
                    hasScrolled = true;
                }

                container.scrollLeft = newScrollLeft;
            });
        };

        // Touch events for mobile
        const onTouchStart = (e) => {
            // Check if container is scrollable
            const hasScroll = container.scrollWidth > container.clientWidth;
            if (!hasScroll) return;

            e.preventDefault();
            e.stopImmediatePropagation();
            dragging = true;
            hasScrolled = false;
            setIsDragging(true);
            startX = e.touches[0].pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;

            // Add dragging class
            if (cssModuleRef.current?.dragging) {
                container.classList.add(cssModuleRef.current.dragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.add(draggingClassName);
            }
        };

        const onTouchEnd = (e) => {
            if (dragging) {
                e.stopImmediatePropagation();
            }

            dragging = false;
            setIsDragging(false);

            // Remove dragging class
            if (cssModuleRef.current?.dragging) {
                container.classList.remove(cssModuleRef.current.dragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.remove(draggingClassName);
            }

            // Reset hasScrolled after a short delay to allow click handlers to check it
            setTimeout(() => {
                hasScrolled = false;
            }, 100);
        };

        const onTouchMove = (e) => {
            if (!dragging) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
                const x = e.touches[0].pageX - container.offsetLeft;
                const walk = (x - startX) * 1;
                const newScrollLeft = scrollLeft - walk;

                if (Math.abs(newScrollLeft - scrollLeft) > 2) {
                    hasScrolled = true;
                }

                container.scrollLeft = newScrollLeft;
            });
        };

        const onContextMenu = (e) => {
            // Prevent context menu when container is scrollable
            const hasScroll = container.scrollWidth > container.clientWidth;
            if (hasScroll) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };

        container.addEventListener('mousedown', onMouseDown, { passive: false, capture: true });
        container.addEventListener('mouseleave', onMouseLeave, { passive: true });
        container.addEventListener('mouseup', onMouseUp, { passive: false, capture: true });
        container.addEventListener('mousemove', onMouseMove, { passive: false, capture: true });
        container.addEventListener('contextmenu', onContextMenu, { passive: false, capture: true });
        container.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
        container.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });

        // Store hasScrolled flag on container for click handlers to check
        container._dragScrollHasScrolled = () => hasScrolled;

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            container.removeEventListener('mousedown', onMouseDown, { capture: true });
            container.removeEventListener('mouseleave', onMouseLeave);
            container.removeEventListener('mouseup', onMouseUp, { capture: true });
            container.removeEventListener('mousemove', onMouseMove, { capture: true });
            container.removeEventListener('contextmenu', onContextMenu, { capture: true });
            container.removeEventListener('touchstart', onTouchStart, { capture: true });
            container.removeEventListener('touchend', onTouchEnd, { capture: true });
            container.removeEventListener('touchmove', onTouchMove, { capture: true });
            delete container._dragScrollHasScrolled;
        };
    }, [containerRef, draggingClassName]);

    return { isDragging };
};

/**
 * Custom hook for vertical drag-to-scroll functionality
 * @param {Object} containerRef - Ref object for the scrollable container
 * @param {string|Object} draggingClassName - CSS class name or CSS module object to apply when dragging (optional)
 * @returns {Object} - Object containing { isDragging }
 */
export const useDragScrollVertical = (containerRef, draggingClassName = null) => {
    const [isDragging, setIsDragging] = useState(false);
    const cssModuleRef = useRef(null);

    useEffect(() => {
        const container = containerRef?.current;
        if (!container) return;

        // Store CSS module reference if provided (for classNames with CSS modules)
        // Check for itemsListDragging property (vertical) or dragging property (horizontal)
        if (typeof draggingClassName === 'object') {
            if (draggingClassName?.itemsListDragging || draggingClassName?.dragging) {
                cssModuleRef.current = draggingClassName;
            }
        }

        let rafId = null;
        let dragging = false;
        let startY = 0;
        let startScrollTop = 0;
        let hasScrolled = false;
        let isActualDrag = false; // Track if user actually dragged (moved mouse)

        const onMouseDown = (e) => {
            // Only handle left mouse button
            if (e.button !== 0) return;

            // Check if container is scrollable vertically
            const hasScroll = container.scrollHeight > container.clientHeight;
            if (!hasScroll) return;

            // Don't prevent default immediately - allow link clicks if no drag occurs
            dragging = true;
            hasScrolled = false;
            isActualDrag = false;
            setIsDragging(true);
            startY = e.clientY;
            startScrollTop = container.scrollTop;
        };

        const onMouseLeave = () => {
            if (dragging) {
                dragging = false;
                isActualDrag = false;
                hasScrolled = false;
                setIsDragging(false);

                // Remove dragging class
                if (cssModuleRef.current?.itemsListDragging) {
                    container.classList.remove(cssModuleRef.current.itemsListDragging);
                } else if (draggingClassName && typeof draggingClassName === 'string') {
                    container.classList.remove(draggingClassName);
                }
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            }
        };

        const onMouseUp = (e) => {
            if (dragging && isActualDrag) {
                e.stopImmediatePropagation();
            }

            // Only prevent link click if user actually dragged
            if (isActualDrag) {
                e.preventDefault();
            }

            dragging = false;
            isActualDrag = false;
            setIsDragging(false);

            // Remove dragging class
            if (cssModuleRef.current?.itemsListDragging) {
                container.classList.remove(cssModuleRef.current.itemsListDragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.remove(draggingClassName);
            }
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            // Reset hasScrolled after a short delay to allow click handlers to check it
            setTimeout(() => {
                hasScrolled = false;
            }, 100);
        };

        const onMouseMove = (e) => {
            if (!dragging) return;

            const deltaY = Math.abs(e.clientY - startY);

            // Only start actual dragging if mouse moved more than 3px
            if (deltaY > 3 && !isActualDrag) {
                isActualDrag = true;
                // Now add dragging class and prevent default
                if (cssModuleRef.current?.itemsListDragging) {
                    container.classList.add(cssModuleRef.current.itemsListDragging);
                } else if (draggingClassName && typeof draggingClassName === 'string') {
                    container.classList.add(draggingClassName);
                }
                document.body.style.cursor = 'grabbing';
                document.body.style.userSelect = 'none';
            }

            if (!isActualDrag) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
                const y = e.clientY;
                const walk = y - startY;
                const newScrollTop = startScrollTop - walk;

                // Clamp scroll position to valid range
                const maxScroll = container.scrollHeight - container.clientHeight;
                const clampedScrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));

                if (Math.abs(clampedScrollTop - container.scrollTop) > 1) {
                    hasScrolled = true;
                }

                container.scrollTop = clampedScrollTop;
            });
        };

        // Touch events for mobile
        const onTouchStart = (e) => {
            // Check if container is scrollable vertically
            const hasScroll = container.scrollHeight > container.clientHeight;
            if (!hasScroll) return;

            e.preventDefault();
            e.stopImmediatePropagation();
            dragging = true;
            hasScrolled = false;
            setIsDragging(true);
            startY = e.touches[0].clientY;
            startScrollTop = container.scrollTop;

            // Add dragging class
            if (cssModuleRef.current?.itemsListDragging) {
                container.classList.add(cssModuleRef.current.itemsListDragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.add(draggingClassName);
            }
        };

        const onTouchEnd = (e) => {
            if (dragging) {
                e.stopImmediatePropagation();
            }

            dragging = false;
            setIsDragging(false);

            // Remove dragging class
            if (cssModuleRef.current?.itemsListDragging) {
                container.classList.remove(cssModuleRef.current.itemsListDragging);
            } else if (draggingClassName && typeof draggingClassName === 'string') {
                container.classList.remove(draggingClassName);
            }

            // Reset hasScrolled after a short delay to allow click handlers to check it
            setTimeout(() => {
                hasScrolled = false;
            }, 100);
        };

        const onTouchMove = (e) => {
            if (!dragging) return;

            e.preventDefault();
            e.stopImmediatePropagation();

            if (rafId) {
                cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
                const y = e.touches[0].clientY;
                const walk = y - startY;
                const newScrollTop = startScrollTop - walk;

                // Clamp scroll position to valid range
                const maxScroll = container.scrollHeight - container.clientHeight;
                const clampedScrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));

                if (Math.abs(clampedScrollTop - container.scrollTop) > 1) {
                    hasScrolled = true;
                }

                container.scrollTop = clampedScrollTop;
            });
        };

        const onContextMenu = (e) => {
            // Prevent context menu when container is scrollable
            const hasScroll = container.scrollHeight > container.clientHeight;
            if (hasScroll) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        };

        // Document-level handlers for mousemove and mouseup to track dragging outside container
        const documentMouseMove = (e) => {
            if (dragging) {
                onMouseMove(e);
            }
        };

        const documentMouseUp = (e) => {
            if (dragging) {
                onMouseUp(e);
            }
        };

        container.addEventListener('mousedown', onMouseDown, { passive: false, capture: true });
        container.addEventListener('mouseleave', onMouseLeave, { passive: true });
        document.addEventListener('mousemove', documentMouseMove, { passive: false, capture: true });
        document.addEventListener('mouseup', documentMouseUp, { passive: false, capture: true });
        container.addEventListener('contextmenu', onContextMenu, { passive: false, capture: true });
        container.addEventListener('touchstart', onTouchStart, { passive: false, capture: true });
        container.addEventListener('touchend', onTouchEnd, { passive: false, capture: true });
        container.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });

        // Store hasScrolled flag on container for click handlers to check
        container._dragScrollHasScrolled = () => hasScrolled;

        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            container.removeEventListener('mousedown', onMouseDown, { capture: true });
            container.removeEventListener('mouseleave', onMouseLeave);
            document.removeEventListener('mousemove', documentMouseMove, { capture: true });
            document.removeEventListener('mouseup', documentMouseUp, { capture: true });
            container.removeEventListener('contextmenu', onContextMenu, { capture: true });
            container.removeEventListener('touchstart', onTouchStart, { capture: true });
            container.removeEventListener('touchend', onTouchEnd, { capture: true });
            container.removeEventListener('touchmove', onTouchMove, { capture: true });
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            delete container._dragScrollHasScrolled;
        };
    }, [containerRef, draggingClassName]);

    return { isDragging };
};

